/**
 * Customizer Bead Path Module
 * Reusable functionality for rendering beads along SVG paths
 *
 * This module provides functionality for:
 * - Rendering letter beads, spacer beads, and blank beads along SVG paths
 * - Responsive scaling and positioning
 * - Path-based rotation and lighting effects
 * - Random perpendicular offset for natural variation
 *
 * Templates that need bead path functionality can import this module
 */

window.RS = window.RS || {};

window.RSBeadPath = (function () {
  // Constants
  const LETTER_BEAD_SIZE_MULTIPLIER = 1.2;
  const IMAGE_OPTIMIZATION_SIZE = 150; // Letter beads are 20% larger than regular beads

  // Renderer Strategies
  const HeightBasedRenderer = {
    calculateLetterDimensions(letterData, beadConfig) {
      const letterHeight = beadConfig.letter_height || 80;
      let letterWidth = letterHeight;

      const aspectRatio = letterData?.aspectRatio || letterData?.aspect_ratio;
      if (aspectRatio && aspectRatio > 0) {
        letterWidth = letterHeight * aspectRatio;
      }

      return { width: letterWidth, height: letterHeight };
    },

    calculateSpacerDimensions(spacerData, beadConfig) {
      const spacerHeight = beadConfig.spacer_height || beadConfig.bead_height || 80;
      let spacerWidth = spacerHeight;

      const aspectRatio = spacerData?.aspectRatio || spacerData?.aspect_ratio;
      if (aspectRatio && aspectRatio > 0) {
        spacerWidth = spacerHeight * aspectRatio;
      }

      return { width: spacerWidth, height: spacerHeight };
    },

    calculateBlankBeadDimensions(beadConfig) {
      const beadHeight = beadConfig.bead_height || 80;
      let beadWidth = beadHeight;

      // Handle both array and object formats for beads
      let beadData = null;
      if (Array.isArray(beadConfig.beads) && beadConfig.beads.length > 0) {
        [beadData] = beadConfig.beads;
      } else if (beadConfig.beads && typeof beadConfig.beads === 'object') {
        [beadData] = Object.values(beadConfig.beads);
      }

      const aspectRatio = beadData?.aspectRatio || beadData?.aspect_ratio;
      if (aspectRatio && aspectRatio > 0) {
        beadWidth = beadHeight * aspectRatio;
      }

      return { width: beadWidth, height: beadHeight };
    },
  };

  const WidthBasedRenderer = {
    calculateLetterDimensions(letterData, beadConfig) {
      const letterWidth = beadConfig.letter_width
        || (beadConfig.bead_width * LETTER_BEAD_SIZE_MULTIPLIER);
      let letterHeight = letterWidth;

      const aspectRatio = letterData?.aspectRatio || letterData?.aspect_ratio;
      if (aspectRatio && aspectRatio > 0) {
        letterHeight = letterWidth / aspectRatio;
      } else if (beadConfig.letter_height && beadConfig.letter_height > 0) {
        letterHeight = beadConfig.letter_height;
      }

      return { width: letterWidth, height: letterHeight };
    },

    calculateSpacerDimensions(spacerData, beadConfig) {
      const spacerWidth = beadConfig.spacer_width || (beadConfig.bead_width * 0.5);
      let spacerHeight = spacerWidth;

      const aspectRatio = spacerData?.aspectRatio || spacerData?.aspect_ratio;
      if (aspectRatio && aspectRatio > 0) {
        spacerHeight = spacerWidth / aspectRatio;
      } else {
        spacerHeight = spacerWidth;
      }

      return { width: spacerWidth, height: spacerHeight };
    },

    calculateBlankBeadDimensions(beadConfig) {
      const beadWidth = beadConfig.bead_width || 150;
      let beadHeight = beadWidth;

      // Handle both array and object formats for beads
      let beadData = null;
      if (Array.isArray(beadConfig.beads) && beadConfig.beads.length > 0) {
        [beadData] = beadConfig.beads;
      } else if (beadConfig.beads && typeof beadConfig.beads === 'object') {
        [beadData] = Object.values(beadConfig.beads);
      }

      const aspectRatio = beadData?.aspectRatio || beadData?.aspect_ratio;
      if (aspectRatio && aspectRatio > 0) {
        beadHeight = beadWidth / aspectRatio;
      } else {
        beadHeight = beadWidth;
      }

      return { width: beadWidth, height: beadHeight };
    },
  };

  function getRenderer(beadConfig) {
    const isHeightBased = (beadConfig.bead_height && beadConfig.bead_height > 0)
      && (!beadConfig.bead_width || beadConfig.bead_width <= 0);

    return isHeightBased ? HeightBasedRenderer : WidthBasedRenderer;
  }

  /**
   * Optimize image URL at rendering time based on bead configuration
   * This is where we have intimate knowledge of the rendering system
   */
  function optimizeImageUrl(imageUrl, letterData, beadConfig) {
    if (!imageUrl) {
      return imageUrl;
    }

    // STRIP existing optimization parameters to apply correct ones
    let cleanUrl = imageUrl;
    if (imageUrl.includes('width=') || imageUrl.includes('height=')) {
      // Remove existing width/height parameters
      cleanUrl = imageUrl.replace(/[&?](width|height)=\d+/g, '');
      // Clean up any trailing ? or &
      cleanUrl = cleanUrl.replace(/[?&]$/, '');
    }

    const separator = cleanUrl.includes('?') ? '&' : '?';

    // Determine rendering mode using the same logic as getRenderer
    const isHeightBased = (beadConfig.bead_height && beadConfig.bead_height > 0)
      && (!beadConfig.bead_width || beadConfig.bead_width <= 0);

    if (isHeightBased) {
      // Height-based rendering: always use height parameter
      return `${cleanUrl}${separator}height=${IMAGE_OPTIMIZATION_SIZE}`;
    }

    // Width-based rendering: choose based on aspect ratio
    const width = letterData?.width;
    const height = letterData?.height;

    if (width && height && width > 0 && height > 0) {
      if (width >= height) {
        return `${cleanUrl}${separator}width=${IMAGE_OPTIMIZATION_SIZE}`;
      }
      return `${cleanUrl}${separator}height=${IMAGE_OPTIMIZATION_SIZE}`;
    }

    // Fallback to width-based
    return `${cleanUrl}${separator}width=${IMAGE_OPTIMIZATION_SIZE}`;
  }

  const config = {
    shadow: {
      // Container shadow configuration
      distance: 16, // Base shadow distance (scaled by container size)
      opacity: 0.2, // Shadow opacity (0.0 to 1.0)
      blur: 3, // Shadow blur radius for softness (scaled by container size)
    },
    // randomOffset config removed - now controlled entirely by metaobject random_offset_ratio
  };

  function decodeEscapeSequence(str) {
    const regexEscapeSequence = /\\u([0-9a-fA-F]{4})|\\x([0-9a-fA-F]{2})|\\([0-7]{3})/g;
    const replacer = (_match, grp1, grp2, grp3) => {
      const codePoint = parseInt(grp3 || (grp2 || grp1), 16);
      return String.fromCharCode(codePoint);
    };
    return str.replace(regexEscapeSequence, replacer);
  }

  function getMetaobjectKey(char, availableLetters = {}) {
    if (availableLetters && availableLetters[char]) {
      return char;
    }

    // Check icon mapping for Unicode -> title conversion
    if (window.RS?.iconMapping?.[char]) {
      return window.RS.iconMapping[char];
    }

    if (availableLetters) {
      const keys = Object.keys(availableLetters);
      const matchingKey = keys.find((key) => key.toLowerCase() === char.toLowerCase());
      if (matchingKey) {
        return matchingKey;
      }
    }

    return char.toUpperCase();
  }

  function fixDoubleEscapedUnicode(str) {
    if (typeof str !== 'string') return str;

    let result = str;

    // Convert double-escaped unicode sequences like \\uf12c to actual unicode characters
    if (str.includes('\\\\u')) {
      result = str.replace(/\\\\u([0-9a-fA-F]{4})/g, (match, hexCode) => {
        const charCode = parseInt(hexCode, 16);
        return String.fromCharCode(charCode);
      });
    }

    // Handle single-escaped unicode sequences
    if (result === str && str.includes('\\u') && !str.includes('\\\\u')) {
      try {
        result = str.replace(/\\u([0-9a-fA-F]{4})/g, (match, hexCode) => {
          const charCode = parseInt(hexCode, 16);
          return String.fromCharCode(charCode);
        });
      } catch (e) {
        // Silently fail and return original string
      }
    }

    return result;
  }

  function hex2rgb(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r, g, b };
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function processTextWithIconMapping(text) {
    if (!text) return '';

    let processedText = '';
    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];

      // Check if this character has an icon mapping (Unicode -> title)
      if (window.RS?.iconMapping?.[char]) {
        const mappedTitle = window.RS.iconMapping[char];
        processedText += mappedTitle;
      } else {
        processedText += char;
      }
    }

    return processedText;
  }

  function tokenizeText(text, availableLetters = {}) {
    if (!text) return [];

    const tokens = [];
    const availableKeys = Object.keys(availableLetters);
    let i = 0;

    while (i < text.length) {
      let matched = false;

      // Check for special tokens (longest first to avoid partial matches)
      const sortedKeys = availableKeys.sort((a, b) => b.length - a.length);

      for (let j = 0; j < sortedKeys.length; j += 1) {
        const key = sortedKeys[j];
        if (text.substr(i, key.length) === key) {
          tokens.push(key);
          i += key.length;
          matched = true;
          break;
        }
      }

      // If no special token matched, treat as single character
      if (!matched) {
        const char = text[i];
        const upperChar = char.toUpperCase();

        // Only add the character if it exists in availableLetters
        // This prevents invalid characters from being added to the customizer
        if (availableLetters[upperChar] || availableLetters[char]) {
          tokens.push(upperChar);
        }
        // If character doesn't exist in GraphQL data, skip it (don't add to tokens)

        i += 1;
      }
    }

    // Filter out empty tokens
    const filteredTokens = tokens.filter((token) => token && token.trim());
    return filteredTokens;
  }

  function getResponsiveScale(container, beadConfig) {
    if (!container) return 1;
    // Get the actual container width
    const containerWidth = container.offsetWidth || container.getBoundingClientRect().width;

    // Calculate scale based on container width vs SVG viewBox width
    const baseWidth = beadConfig.svg_view_box_width || 1110;
    const scale = containerWidth / baseWidth;
    return scale;
  }

  function createSVGContainer(container, beadConfig, containerClass = 'bead-path-svg-container') {
    let svgContainer = container.querySelector(`.${containerClass}`);
    if (!svgContainer) {
      svgContainer = document.createElement('div');
      svgContainer.className = containerClass;
      svgContainer.style.position = 'absolute';
      svgContainer.style.top = '0';
      svgContainer.style.left = '0';
      svgContainer.style.width = '100%';
      svgContainer.style.height = '100%';
      svgContainer.style.pointerEvents = 'none';
      svgContainer.style.zIndex = '1';
      container.appendChild(svgContainer);
    }

    // Create SVG element
    let svg = svgContainer.querySelector('svg');
    if (!svg) {
      svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.setAttribute('viewBox', `0 0 ${beadConfig.svg_view_box_width} ${beadConfig.svg_view_box_height}`);
      svg.style.position = 'absolute';
      svg.style.top = '0';
      svg.style.left = '0';
      svgContainer.appendChild(svg);
    }

    return svg;
  }

  function createSVGPath(svgContainer, svgGuideContent) {
    // Parse SVG content to extract path data
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = svgGuideContent;
    const pathElement = tempDiv.querySelector('path');

    if (!pathElement) {
      return null;
    }

    const pathData = pathElement.getAttribute('d');
    if (!pathData) {
      return null;
    }

    // Create path element in our SVG
    let guidePath = svgContainer.querySelector('path');
    if (!guidePath) {
      guidePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      guidePath.setAttribute('fill', 'none');
      guidePath.setAttribute('stroke', 'transparent');
      guidePath.setAttribute('stroke-width', '1');
      svgContainer.appendChild(guidePath);
    }

    guidePath.setAttribute('d', pathData);
    return guidePath;
  }

  function createBeadContainer(container, containerClass = 'bead-path-bead-container') {
    let beadContainer = container.querySelector(`.${containerClass}`);
    if (!beadContainer) {
      beadContainer = document.createElement('div');
      beadContainer.className = containerClass;
      beadContainer.style.position = 'absolute';
      beadContainer.style.top = '0';
      beadContainer.style.left = '0';
      beadContainer.style.width = '100%';
      beadContainer.style.height = '100%';
      beadContainer.style.pointerEvents = 'none';
      beadContainer.style.zIndex = '10';
      container.appendChild(beadContainer);
    }
    return beadContainer;
  }

  function getLightSourcePosition(beadConfig, container) {
    const lightSourceAngle = beadConfig.light_source_angle || 40;
    const scale = getResponsiveScale(container, beadConfig);

    // Convert angle to radians
    const angleRad = (lightSourceAngle * Math.PI) / 180;

    // Place light source far away (1000x distance) in the direction of the angle
    const distance = 1000 * scale;
    const containerWidth = container.offsetWidth || container.getBoundingClientRect().width;
    const containerHeight = container.offsetHeight || container.getBoundingClientRect().height;
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    // Calculate light source position
    // Note: Flip Y-axis to convert from mathematical coordinates to screen coordinates
    const lightSourceX = centerX + Math.cos(angleRad) * distance;
    const lightSourceY = centerY - Math.sin(angleRad) * distance; // Subtract to flip Y-axis

    return { x: lightSourceX, y: lightSourceY };
  }

  function getRegularBeadRotation(beadX, beadY, lightSourceX, lightSourceY) {
    const deltaX = lightSourceX - beadX;
    const deltaY = lightSourceY - beadY;
    // Calculate angle from bead to light source (x-axis pointing toward light)
    return Math.atan2(deltaY, deltaX) * (180 / Math.PI);
  }

  function getPathBasedRotation(pathElement, position) {
    if (!pathElement) return 0;

    // Get two points very close together to calculate path direction
    const offset = 0.001; // Small offset to calculate tangent
    const pathLength = pathElement.getTotalLength();
    const pos1 = Math.max(0, Math.min(pathLength, (position / pathLength - offset) * pathLength));
    const pos2 = Math.max(0, Math.min(pathLength, (position / pathLength + offset) * pathLength));

    const point1 = pathElement.getPointAtLength(pos1);
    const point2 = pathElement.getPointAtLength(pos2);

    const deltaX = point2.x - point1.x;
    const deltaY = point2.y - point1.y;

    // Calculate angle of path direction and rotate 180 degrees
    // This makes +y axis face inward toward the center of the path
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 180;

    return angle;
  }

  function getPathTangentRotation(pathElement, position) {
    if (!pathElement) return 0;

    // Get two points very close together to calculate path direction
    const offset = 0.001; // Small offset to calculate tangent
    const pathLength = pathElement.getTotalLength();
    const pos1 = Math.max(0, Math.min(pathLength, (position / pathLength - offset) * pathLength));
    const pos2 = Math.max(0, Math.min(pathLength, (position / pathLength + offset) * pathLength));

    const point1 = pathElement.getPointAtLength(pos1);
    const point2 = pathElement.getPointAtLength(pos2);

    const deltaX = point2.x - point1.x;
    const deltaY = point2.y - point1.y;

    // Calculate angle of path direction - x-axis follows path tangent exactly
    // Allow +y axis to face toward the center of the path
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

    return angle;
  }

  function applyPerpendicularRandomOffset(pathElement, position, originalPoint, scale, beadConfig) {
    if (!pathElement) return { x: originalPoint.x * scale, y: originalPoint.y * scale };

    // Calculate path tangent direction at this position
    const offset = 0.001; // Small offset to calculate tangent
    const pathLength = pathElement.getTotalLength();
    const pos1 = Math.max(0, Math.min(pathLength, position - offset));
    const pos2 = Math.max(0, Math.min(pathLength, position + offset));

    const point1 = pathElement.getPointAtLength(pos1);
    const point2 = pathElement.getPointAtLength(pos2);

    // Calculate tangent vector (direction along the path)
    const tangentX = point2.x - point1.x;
    const tangentY = point2.y - point1.y;

    // Normalize tangent vector
    const tangentLength = Math.sqrt(tangentX * tangentX + tangentY * tangentY);
    if (tangentLength === 0) {
      return { x: originalPoint.x * scale, y: originalPoint.y * scale };
    }

    const normalizedTangentX = tangentX / tangentLength;
    const normalizedTangentY = tangentY / tangentLength;

    // Calculate perpendicular (normal) vector by rotating tangent 90 degrees
    // Perpendicular vector = (-tangentY, tangentX)
    const perpendicularX = -normalizedTangentY;
    const perpendicularY = normalizedTangentX;

    // Simple random offset calculation
    if (!beadConfig.random_offset) {
      return { x: originalPoint.x * scale, y: originalPoint.y * scale };
    }

    // Use offset_factor (0-10 range) for intensity, with fallback to 0.02 (2%)
    let offsetRatio = 0.02; // Default 2% intensity
    if (typeof beadConfig.offset_factor === 'number') {
      // Convert 0-10 range to 0.0-0.15 range for noticeable but subtle intensity
      // Adjusted for width-based products with smaller bead dimensions
      offsetRatio = Math.max(0, Math.min(0.15, beadConfig.offset_factor / 67));
    }

    const beadWidth = beadConfig.bead_width || 150;
    const maxOffset = beadWidth * offsetRatio * scale;

    // Stable random based on position
    const seed = Math.sin(position * 12.9898) * 43758.5453;
    const random = seed - Math.floor(seed);
    const randomOffset = (random - 0.5) * 2 * maxOffset;

    // Apply the perpendicular offset to the original position
    const scaledX = (originalPoint.x * scale) + (perpendicularX * randomOffset);
    const scaledY = (originalPoint.y * scale) + (perpendicularY * randomOffset);

    return { x: scaledX, y: scaledY };
  }

  function calculateBeadDimensions(beadConfig) {
    const renderer = getRenderer(beadConfig);
    return renderer.calculateBlankBeadDimensions(beadConfig);
  }

  function applyDropShadowToContainer(container, beadConfig) {
    if (!container || !beadConfig) return;

    const lightSourceAngle = beadConfig.light_source_angle || 44;
    const scale = getResponsiveScale(container, beadConfig);

    // Convert angle to radians
    const angleRad = (lightSourceAngle * Math.PI) / 180;

    // Use template-specific shadow settings if provided, otherwise use defaults
    const shadowConfig = beadConfig.shadow || config.shadow;
    // Calculate shadow offset (opposite direction from light source)
    // Use fixed distance for bead-mine-kids, scaled for others
    const baseShadowDistance = beadConfig.shadow
      ? shadowConfig.distance // Fixed distance for custom shadows
      : shadowConfig.distance * scale; // Scaled distance for default shadows
    // Shadow direction is opposite to light source, accounting for screen coordinate Y-flip
    const shadowX = Math.cos(angleRad + Math.PI) * baseShadowDistance;
    // Flip Y for screen coordinates
    const shadowY = -Math.sin(angleRad + Math.PI) * baseShadowDistance;

    // Calculate blur radius based on scale and config
    const blurRadius = beadConfig.shadow
      ? shadowConfig.blur // Fixed blur for custom shadows
      : shadowConfig.blur * scale; // Scaled blur for default shadows

    // Force hardware acceleration to help Safari render negative drop-shadow values
    container.style.willChange = 'filter';
    container.style.transform = `${container.style.transform || ''} translateZ(0)`.trim();
    // Apply the original shadow calculation (including negative values)
    const shadowFilter = `drop-shadow(${shadowX.toFixed(2)}px ${shadowY.toFixed(2)}px ${blurRadius.toFixed(2)}px rgba(0, 0, 0, ${shadowConfig.opacity}))`;

    // Preserve any existing filters and add shadow
    const currentFilter = container.style.filter || '';
    const filterWithoutShadow = currentFilter.replace(/drop-shadow\([^)]*rgba\([^)]*\)[^)]*\)/g, '').trim();
    const newFilter = filterWithoutShadow ? `${filterWithoutShadow} ${shadowFilter}` : shadowFilter;

    container.style.filter = newFilter;
  }

  function renderLetterBead(beadContainer, pathElement, letter, position, beadConfig, availableLetters, beadClass = 'bead-path-letter-bead') {
    const point = pathElement.getPointAtLength(position);
    const letterData = availableLetters?.[letter];

    // If no specific letter data, skip rendering (don't render fallback for special tokens)
    if (!letterData) {
      return;
    }

    // Extract image URL from the new structure (backward compatible)
    const rawImageUrl = typeof letterData === 'string' ? letterData : letterData.url;

    if (!rawImageUrl) {
      return;
    }

    // OPTIMIZATION AT RENDERING STAGE: Apply width/height based optimization
    const letterImageUrl = optimizeImageUrl(rawImageUrl, letterData, beadConfig);

    // Calculate responsive scale
    const container = beadContainer.closest('.customizer-view');
    const scale = getResponsiveScale(container, beadConfig);

    // Calculate letter dimensions using centralized renderer system
    const renderer = getRenderer(beadConfig);
    const dimensions = renderer.calculateLetterDimensions(letterData, beadConfig);
    const letterWidth = dimensions.width;
    const letterHeight = dimensions.height;

    const scaledLetterWidth = letterWidth * scale;
    const scaledLetterHeight = letterHeight * scale;
    const beadOffsetX = scaledLetterWidth / 2;
    const beadOffsetY = scaledLetterHeight / 2;

    const beadElement = document.createElement('div');
    beadElement.className = beadClass;
    beadElement.setAttribute('data-letter', letter);
    beadElement.style.position = 'absolute';
    beadElement.style.width = `${scaledLetterWidth}px`;
    beadElement.style.height = `${scaledLetterHeight}px`;

    // Apply responsive scaling to positions with optional random offset
    let scaledX = point.x * scale;
    let scaledY = point.y * scale;

    // Apply random offset perpendicular to path tangent
    const offsetPoint = applyPerpendicularRandomOffset(
      pathElement,
      position,
      point,
      scale,
      beadConfig,
    );
    scaledX = offsetPoint.x;
    scaledY = offsetPoint.y;

    const scaledYOffset = beadConfig.y_offset * scale;

    beadElement.style.left = `${scaledX - beadOffsetX}px`;
    beadElement.style.top = `${scaledY - beadOffsetY + scaledYOffset}px`;
    beadElement.style.transformOrigin = 'center center';
    beadElement.style.transition = 'transform 0.2s ease';
    beadElement.style.zIndex = '15'; // Higher than blank beads

    // Use letter-specific background image (no text overlay)
    beadElement.style.backgroundImage = `url(${letterImageUrl})`;
    beadElement.style.backgroundSize = 'contain';
    beadElement.style.backgroundRepeat = 'no-repeat';
    beadElement.style.backgroundPosition = 'center';

    // Calculate path-based rotation for letter beads with configurable offset
    const rotationOffset = beadConfig.bead_rotation_offset !== undefined
      ? beadConfig.bead_rotation_offset : 180;
    const rotation = getPathTangentRotation(pathElement, position) + rotationOffset;
    beadElement.style.transform = `rotate(${rotation}deg)`;

    beadContainer.appendChild(beadElement);
  }

  function shouldRenderSpacers(beadConfig) {
    // Don't render spacers if:
    // 1. spacer object is empty
    // 2. no spacer data available
    if (!beadConfig.spacer || Object.keys(beadConfig.spacer).length === 0) {
      return false;
    }

    const spacerData = Object.values(beadConfig.spacer)[0];
    if (!spacerData) {
      return false;
    }

    // Handle both old string format and new object format
    const spacerImageUrl = typeof spacerData === 'string' ? spacerData : spacerData?.url;
    if (!spacerImageUrl) {
      return false;
    }

    // For width-based rendering with square beads, allow spacers even if spacer_width is null/0
    // The rendering logic can calculate dimensions from aspect ratio or fall back to bead_width
    return true;
  }

  function renderSpacerBead(beadContainer, pathElement, position, beadConfig, beadClass = 'bead-path-spacer-bead') {
    // Check if spacers should be rendered for this configuration
    if (!shouldRenderSpacers(beadConfig)) {
      return;
    }

    const point = pathElement.getPointAtLength(position);
    const spacerData = Object.values(beadConfig.spacer)[0];

    // Handle both old string format and new object format
    const rawSpacerImageUrl = typeof spacerData === 'string' ? spacerData : spacerData?.url;

    if (!rawSpacerImageUrl) return;

    // OPTIMIZATION AT RENDERING STAGE: Apply width/height based optimization for spacer beads
    const spacerImageUrl = optimizeImageUrl(rawSpacerImageUrl, spacerData, beadConfig);

    // Calculate responsive scale
    const container = beadContainer.closest('.customizer-view');
    const scale = getResponsiveScale(container, beadConfig);

    // Initialize spacer dimensions - will be set by priority logic
    let spacerWidth;

    // Calculate spacer dimensions with proper aspect ratio handling

    let spacerHeight;

    // Priority 1: Both width and height explicitly set (> 0)
    if (beadConfig.spacer_width != null && beadConfig.spacer_width > 0
        && beadConfig.spacer_height != null && beadConfig.spacer_height > 0) {
      spacerWidth = beadConfig.spacer_width;
      spacerHeight = beadConfig.spacer_height;
    } else if (beadConfig.spacer_height != null && beadConfig.spacer_height > 0
             && (beadConfig.spacer_width == null || beadConfig.spacer_width <= 0)
             && spacerData && spacerData.aspect_ratio && spacerData.aspect_ratio > 0) {
      // Priority 2: Height set, calculate width from aspect ratio (width is null or 0)
      spacerHeight = beadConfig.spacer_height;
      spacerWidth = spacerHeight * spacerData.aspect_ratio;
    } else if (beadConfig.spacer_width != null && beadConfig.spacer_width > 0
             && (beadConfig.spacer_height == null || beadConfig.spacer_height <= 0)
             && spacerData && spacerData.aspect_ratio && spacerData.aspect_ratio > 0) {
      // Priority 3: Width set, calculate height from aspect ratio (height is null or 0)
      spacerWidth = beadConfig.spacer_width;
      spacerHeight = spacerWidth / spacerData.aspect_ratio;
    } else if (beadConfig.spacer_height != null && beadConfig.spacer_height > 0) {
      // Priority 4: Use explicit height only (square fallback for width)
      spacerHeight = beadConfig.spacer_height;
      spacerWidth = spacerHeight;
    } else {
      // Priority 5: Fall back to default dimensions
      spacerWidth = beadConfig.spacer_width != null && beadConfig.spacer_width > 0
        ? beadConfig.spacer_width
        : (beadConfig.bead_width || 150);
      spacerHeight = spacerWidth;
    }

    const scaledSpacerWidth = spacerWidth * scale;
    const scaledSpacerHeight = spacerHeight * scale;
    const spacerOffsetX = scaledSpacerWidth / 2;
    const spacerOffsetY = scaledSpacerHeight / 2;

    const spacerElement = document.createElement('div');
    spacerElement.className = beadClass;
    spacerElement.style.position = 'absolute';
    spacerElement.style.width = `${scaledSpacerWidth}px`;
    spacerElement.style.height = `${scaledSpacerHeight}px`;

    // Apply responsive scaling to positions with optional random offset
    let scaledX = point.x * scale;
    let scaledY = point.y * scale;

    // Apply random offset perpendicular to path tangent
    const offsetPoint = applyPerpendicularRandomOffset(
      pathElement,
      position,
      point,
      scale,
      beadConfig,
    );
    scaledX = offsetPoint.x;
    scaledY = offsetPoint.y;

    const scaledYOffset = beadConfig.y_offset * scale;

    spacerElement.style.left = `${scaledX - spacerOffsetX}px`;
    spacerElement.style.top = `${scaledY - spacerOffsetY + scaledYOffset}px`;
    spacerElement.style.backgroundImage = `url(${spacerImageUrl})`;
    spacerElement.style.backgroundSize = 'contain';
    spacerElement.style.backgroundRepeat = 'no-repeat';
    spacerElement.style.backgroundPosition = 'center';
    spacerElement.style.transformOrigin = 'center center';
    spacerElement.style.transition = 'transform 0.2s ease';
    spacerElement.style.zIndex = '12'; // Between blank beads (10) and letter beads (15)

    // Calculate path-based rotation for spacer beads with configurable offset
    const rotationOffset = beadConfig.bead_rotation_offset !== undefined
      ? beadConfig.bead_rotation_offset : 180;
    const rotation = getPathTangentRotation(pathElement, position) + rotationOffset;
    spacerElement.style.transform = `rotate(${rotation}deg)`;

    beadContainer.appendChild(spacerElement);
  }

  function renderBlankBead(beadContainer, pathElement, position, beadConfig, beadClass = 'bead-path-blank-bead', beadIndex = 0) {
    const point = pathElement.getPointAtLength(position);

    // Handle both array and object formats for beads
    let beadData = null;
    let beadArrayIndex = 0;
    if (Array.isArray(beadConfig.beads) && beadConfig.beads.length > 0) {
      // Cycle through available beads based on position/index
      beadArrayIndex = beadIndex % beadConfig.beads.length;
      beadData = beadConfig.beads[beadArrayIndex];
    } else if (beadConfig.beads && typeof beadConfig.beads === 'object') {
      [beadData] = Object.values(beadConfig.beads);
    }

    // Handle both old string format and new object format
    const rawBeadImage = typeof beadData === 'string' ? beadData : beadData?.url;

    if (!rawBeadImage) return;

    // OPTIMIZATION AT RENDERING STAGE: Apply width/height based optimization for blank beads
    const defaultBeadImage = optimizeImageUrl(rawBeadImage, beadData, beadConfig);

    // Calculate responsive scale
    const container = beadContainer.closest('.customizer-view');
    const scale = getResponsiveScale(container, beadConfig);

    // Calculate bead dimensions using centralized logic
    const dimensions = calculateBeadDimensions(beadConfig);
    const beadWidth = dimensions.width;
    const beadHeight = dimensions.height;

    // Calculate scaled bead dimensions
    const scaledBeadWidth = beadWidth * scale;
    const scaledBeadHeight = beadHeight * scale;
    const beadOffsetX = scaledBeadWidth / 2;
    const beadOffsetY = scaledBeadHeight / 2;

    const beadElement = document.createElement('div');
    beadElement.className = beadClass;
    beadElement.style.position = 'absolute';
    beadElement.style.width = `${scaledBeadWidth}px`;
    beadElement.style.height = `${scaledBeadHeight}px`;

    // Add data attributes for debugging bead ordering
    beadElement.setAttribute('data-bead-index', beadIndex);
    beadElement.setAttribute('data-array-index', beadArrayIndex);
    // Apply responsive scaling to positions with optional random offset
    let scaledX = point.x * scale;
    let scaledY = point.y * scale;

    // Apply random offset perpendicular to path tangent
    const offsetPoint = applyPerpendicularRandomOffset(
      pathElement,
      position,
      point,
      scale,
      beadConfig,
    );
    scaledX = offsetPoint.x;
    scaledY = offsetPoint.y;

    const scaledYOffset = beadConfig.y_offset * scale;

    beadElement.style.left = `${scaledX - beadOffsetX}px`;
    beadElement.style.top = `${scaledY - beadOffsetY + scaledYOffset}px`;
    beadElement.style.backgroundImage = `url(${defaultBeadImage})`;
    beadElement.style.backgroundSize = 'contain';
    beadElement.style.backgroundRepeat = 'no-repeat';
    beadElement.style.backgroundPosition = 'center';
    beadElement.style.transformOrigin = 'center center';
    beadElement.style.transition = 'transform 0.2s ease';
    beadElement.style.zIndex = '10';

    // Calculate rotation for blank beads - use light source if enabled, otherwise path-based
    let rotation;
    if (beadConfig.light_source_orientation) {
      // Use light source rotation for regular beads when enabled
      const lightSource = getLightSourcePosition(beadConfig, container);
      rotation = getRegularBeadRotation(scaledX, scaledY, lightSource.x, lightSource.y);
    } else {
      // Use path tangent rotation with configurable offset (default 180 degrees)
      const rotationOffset = beadConfig.bead_rotation_offset !== undefined
        ? beadConfig.bead_rotation_offset : 180;
      rotation = getPathTangentRotation(pathElement, position) + rotationOffset;
    }
    beadElement.style.transform = `rotate(${rotation}deg)`;

    beadContainer.appendChild(beadElement);
  }

  function clearBeadsInContainer(beadContainer, beadClasses = ['bead-path-blank-bead', 'bead-path-letter-bead', 'bead-path-spacer-bead']) {
    const selector = beadClasses.map((cls) => `.${cls}`).join(', ');
    const allBeads = beadContainer.querySelectorAll(selector);
    allBeads.forEach((bead) => bead.remove());
  }

  function fillRemainingPathWithRegularBeads(
    beadContainer,
    pathElement,
    occupiedPositions,
    beadConfig,
    scale,
    beadClass = 'bead-path-blank-bead',
  ) {
    // Validate beadConfig parameters to prevent infinite loops
    if (!beadConfig) {
      console.warn('Missing beadConfig, skipping regular bead filling');
      return;
    }

    // Validation is now handled by calculateBeadDimensions function

    // Ensure bead height exists
    if (typeof beadConfig.bead_height !== 'number' || beadConfig.bead_height <= 0) {
      beadConfig.bead_height = beadConfig.bead_width;
    }

    const pathLength = pathElement.getTotalLength();
    // Calculate proper bead dimensions first
    const dimensions = calculateBeadDimensions(beadConfig);
    const scaledBeadWidth = dimensions.width * scale;
    const scaledPathLength = pathLength * scale;

    // Apply letter_spacing to blank bead spacing to maintain consistent density
    const baseSpacingMultiplier = 0.95;
    const letterSpacingAdjustment = 1 + ((beadConfig.letter_spacing || 0) / 100);
    const adjustedSpacingMultiplier = baseSpacingMultiplier * letterSpacingAdjustment;
    // Minimum 0.1 to prevent overlap
    const beadSpacing = scaledBeadWidth * Math.max(0.1, adjustedSpacingMultiplier);

    // Additional validation to prevent division by zero and infinite loops
    if (!Number.isFinite(scaledPathLength) || scaledPathLength <= 0
        || !Number.isFinite(beadSpacing) || beadSpacing <= 0) {
      console.warn('Invalid path or spacing calculations, skipping regular bead filling');
      return;
    }

    // Sort occupied positions for processing
    const sortedOccupied = occupiedPositions.sort((a, b) => a.position - b.position);

    // Calculate available segments between occupied positions
    const segments = [];
    if (sortedOccupied.length === 0) {
      // No occupied positions, fill entire path
      segments.push({ start: 0, end: 1 });
    } else {
      // Add segment before first occupied position
      const firstRadius = (sortedOccupied[0].size / 2) / pathLength;
      const firstStart = sortedOccupied[0].position - firstRadius;
      if (firstStart > 0) {
        segments.push({ start: 0, end: firstStart });
      }

      // Add segments between occupied positions
      for (let i = 0; i < sortedOccupied.length - 1; i += 1) {
        const currentRadius = (sortedOccupied[i].size / 2) / pathLength;
        const nextRadius = (sortedOccupied[i + 1].size / 2) / pathLength;
        const currentEnd = sortedOccupied[i].position + currentRadius;
        const nextStart = sortedOccupied[i + 1].position - nextRadius;

        if (nextStart > currentEnd) {
          segments.push({ start: currentEnd, end: nextStart });
        }
      }

      // Add segment after last occupied position
      const lastOccupied = sortedOccupied[sortedOccupied.length - 1];
      const lastRadius = (lastOccupied.size / 2) / pathLength;
      const lastEnd = lastOccupied.position + lastRadius;
      if (lastEnd < 1) {
        segments.push({ start: lastEnd, end: 1 });
      }
    }

    // Fill each segment with regular beads
    segments.forEach((segment) => {
      const segmentLength = (segment.end - segment.start) * scaledPathLength;
      let beadCount = Math.floor(segmentLength / beadSpacing);

      if (beadCount <= 0) return;

      // Prevent excessive bead rendering that could cause performance issues
      if (beadCount > 500) {
        console.warn('Segment bead count exceeds safe limit, limiting to 500 beads');
        beadCount = 500;
      }

      // Distribute beads evenly within the segment
      const actualSpacing = segmentLength / beadCount;

      for (let i = 0; i < beadCount; i += 1) {
        // Calculate relative position within segment
        const relativePosition = ((i + 0.5) * actualSpacing) / scaledPathLength;
        const absolutePosition = segment.start + relativePosition;

        // Convert to path units and render
        const pathPosition = absolutePosition * pathLength;
        renderBlankBead(beadContainer, pathElement, pathPosition, beadConfig, beadClass, i);
      }
    });
  }

  function renderBlankBeadsOnPath(beadContainer, pathElement, beadConfig, beadClass = 'bead-path-blank-bead') {
    // Validate beadConfig parameters to prevent infinite loops
    if (!beadConfig) {
      console.warn('Missing beadConfig, skipping blank bead rendering');
      return;
    }

    // Validation is now handled by calculateBeadDimensions function

    // Ensure bead height exists
    if (typeof beadConfig.bead_height !== 'number' || beadConfig.bead_height <= 0) {
      beadConfig.bead_height = beadConfig.bead_width;
    }

    // Calculate responsive scale
    const container = beadContainer.closest('.customizer-view');
    const scale = getResponsiveScale(container, beadConfig);

    const pathLength = pathElement.getTotalLength();
    // Calculate proper bead dimensions first
    const dimensions = calculateBeadDimensions(beadConfig);
    const scaledBeadWidth = dimensions.width * scale;
    const scaledPathLength = pathLength * scale;

    // Apply letter_spacing to blank bead spacing to maintain consistent density
    const baseSpacingMultiplier = 0.95;
    const letterSpacingAdjustment = 1 + ((beadConfig.letter_spacing || 0) / 100);
    const adjustedSpacingMultiplier = baseSpacingMultiplier * letterSpacingAdjustment;
    // Minimum 0.1 to prevent overlap
    const beadSpacing = scaledBeadWidth * Math.max(0.1, adjustedSpacingMultiplier);

    // Additional validation to prevent division by zero and infinite loops
    if (!Number.isFinite(scaledPathLength) || scaledPathLength <= 0
        || !Number.isFinite(beadSpacing) || beadSpacing <= 0) {
      console.warn('Invalid path or spacing calculations, skipping blank bead rendering');
      return;
    }

    let beadCount = Math.floor(scaledPathLength / beadSpacing);

    // Prevent excessive bead rendering that could cause performance issues
    if (beadCount > 1000) {
      console.warn('Bead count exceeds safe limit, limiting to 1000 beads');
      beadCount = 1000;
    }

    for (let i = 0; i < beadCount; i += 1) {
      // Convert back to path units for positioning
      const pathPosition = (i * beadSpacing) / scale + (dimensions.width / 2);
      renderBlankBead(beadContainer, pathElement, pathPosition, beadConfig, beadClass, i);
    }
  }

  function renderLetterBeadsWithRegularBeads(
    beadContainer,
    pathElement,
    letters,
    beadConfig,
    scale,
    availableLetters,
    letterBeadClass = 'bead-path-letter-bead',
    spacerBeadClass = 'bead-path-spacer-bead',
    blankBeadClass = 'bead-path-blank-bead',
  ) {
    // If no valid letters, render blank beads instead of returning empty
    // This prevents wiping out the customizer when invalid characters are typed
    if (!letters.length) {
      renderBlankBeadsOnPath(beadContainer, pathElement, beadConfig, blankBeadClass);
      return;
    }

    // Validate and normalize beadConfig parameters to prevent infinite loops
    if (!beadConfig) {
      console.warn('Missing beadConfig, skipping bead rendering');
      return;
    }

    // Handle case where heights are defined but widths are not (e.g., tennis bracelet)
    const hasWidth = typeof beadConfig.bead_width === 'number' && beadConfig.bead_width > 0;
    const hasHeight = typeof beadConfig.letter_height === 'number' && beadConfig.letter_height > 0;

    // Allow rendering even if both are null - renderers will use fallback values
    // Only block if we have no way to determine dimensions at all
    if (!hasWidth && !hasHeight && !beadConfig.bead_width && !beadConfig.letter_height) {
      console.warn('No dimension parameters available - cannot render beads');
      return;
    }

    // Determine sizing priority state
    let sizingMode;
    if (hasWidth && hasHeight) {
      sizingMode = 'widthFirst'; // Width takes priority when both are defined
    } else if (hasWidth && !hasHeight) {
      sizingMode = 'widthFirst';
    } else if (!hasWidth && hasHeight) {
      sizingMode = 'heightFirst';
    } else {
      // Both are null/undefined - default to widthFirst for backward compatibility
      sizingMode = 'widthFirst';
    }

    // Apply sizing logic based on priority state
    if (sizingMode === 'widthFirst') {
      // Width-first: bead_width defines regular beads, letter beads are larger
      if (!hasHeight) {
        beadConfig.letter_height = beadConfig.bead_width * LETTER_BEAD_SIZE_MULTIPLIER;
      } else {
        // Both available - ensure letter_height supports larger letter beads
        const expectedLetterHeight = beadConfig.bead_width * LETTER_BEAD_SIZE_MULTIPLIER;
        if (beadConfig.letter_height < expectedLetterHeight) {
          beadConfig.letter_height = expectedLetterHeight;
        }
      }
    } else if (sizingMode === 'heightFirst') {
      // Height-first: letter_height defines letter beads, regular beads are smaller
      // Don't mutate beadConfig - let calculateBeadDimensions handle this
    }

    // Only set spacer fallbacks if they're truly invalid (negative numbers)
    // Allow null and 0 to pass through for aspect ratio calculations
    if (beadConfig.spacer_width !== null
      && (typeof beadConfig.spacer_width !== 'number' || beadConfig.spacer_width < 0)) {
      beadConfig.spacer_width = beadConfig.bead_width * 0.5;
    }
    if (beadConfig.spacer_height !== null
      && (typeof beadConfig.spacer_height !== 'number' || beadConfig.spacer_height < 0)) {
      beadConfig.spacer_height = beadConfig.spacer_width;
    }

    // Ensure bead height exists
    if (typeof beadConfig.bead_height !== 'number' || beadConfig.bead_height <= 0) {
      beadConfig.bead_height = beadConfig.bead_width; // Default to square aspect ratio
    }

    // Validate letter spacing
    if (typeof beadConfig.letter_spacing !== 'number') {
      beadConfig.letter_spacing = 0; // Default to no extra spacing
    }

    // Clear all existing beads first
    clearBeadsInContainer(beadContainer, [letterBeadClass, spacerBeadClass, blankBeadClass]);

    const pathLength = pathElement.getTotalLength();
    const scaledPathLength = pathLength * scale;

    // Additional validation to prevent division by zero
    if (!Number.isFinite(scaledPathLength) || scaledPathLength <= 0) {
      console.warn('Invalid scaledPathLength, skipping bead rendering');
      return;
    }

    // Calculate scaled dimensions for different bead types
    const scaledSpacerWidth = beadConfig.spacer_width * scale;
    const scaledSpacerHeight = beadConfig.spacer_height * scale;

    // Legacy variable names for compatibility with existing code
    const spacerSize = Math.max(scaledSpacerWidth, scaledSpacerHeight);

    // Calculate actual dimensions for each letter using GraphQL data
    const renderer = getRenderer(beadConfig);
    const letterDimensions = letters.map((letter) => {
      const letterData = availableLetters?.[letter];
      if (letterData) {
        return renderer.calculateLetterDimensions(letterData, beadConfig);
      }
      // Fallback to generic dimensions if no GraphQL data
      return {
        width: beadConfig.bead_width * LETTER_BEAD_SIZE_MULTIPLIER,
        height: beadConfig.letter_height || (beadConfig.bead_width * LETTER_BEAD_SIZE_MULTIPLIER),
      };
    });

    // Calculate total width needed for all letters
    const totalLetterWidth = letterDimensions.reduce((sum, dim) => sum + dim.width, 0);

    // Use the maximum letter dimension for general spacing calculations
    // (removed unused maxLetterWidth and maxLetterHeight calculations)

    // Calculate spacer dimensions only if spacers should be rendered
    let spacerBeadWidth = 0;
    let spacerBeadHeight = 0;

    if (shouldRenderSpacers(beadConfig)) {
      const spacerData = Object.values(beadConfig.spacer)[0];

      // Use the same width-first/height-first logic for consistency
      if (beadConfig.spacer_width != null && beadConfig.spacer_width > 0
          && beadConfig.spacer_height != null && beadConfig.spacer_height > 0) {
        // Priority 1: Both width and height explicitly set (> 0)
        spacerBeadWidth = beadConfig.spacer_width;
        spacerBeadHeight = beadConfig.spacer_height;
      } else if (beadConfig.spacer_height != null && beadConfig.spacer_height > 0
               && (beadConfig.spacer_width == null || beadConfig.spacer_width <= 0)
               && spacerData && spacerData.aspect_ratio && spacerData.aspect_ratio > 0) {
        // Priority 2: Height set, calculate width from aspect ratio (width is null or 0)
        spacerBeadHeight = beadConfig.spacer_height;
        spacerBeadWidth = spacerBeadHeight * spacerData.aspect_ratio;
      } else if (beadConfig.spacer_width != null && beadConfig.spacer_width > 0
               && (beadConfig.spacer_height == null || beadConfig.spacer_height <= 0)
               && spacerData && spacerData.aspect_ratio && spacerData.aspect_ratio > 0) {
        // Priority 3: Width set, calculate height from aspect ratio (height is null or 0)
        spacerBeadWidth = beadConfig.spacer_width;
        spacerBeadHeight = spacerBeadWidth / spacerData.aspect_ratio;
      } else if (beadConfig.spacer_height != null && beadConfig.spacer_height > 0) {
        // Priority 4: Use explicit height only (square fallback for width)
        spacerBeadHeight = beadConfig.spacer_height;
        spacerBeadWidth = spacerBeadHeight;
      } else {
        // Priority 5: Fall back to default dimensions for width-based rendering
        // For square beads, use bead_width as the spacer dimension
        spacerBeadWidth = beadConfig.bead_width || 80; // Default if bead_width missing
        spacerBeadHeight = spacerBeadWidth; // Square spacers for width-based rendering
      }
    }

    // Calculate letter spacing value based on the primary bead dimension
    // (removed unused primaryBeadSize calculation)

    // Calculate actual spacing needed based on individual letter dimensions
    let totalContentWidth;
    let gapBetweenLetters;
    let spacerGroupWidth = 0;

    if (shouldRenderSpacers(beadConfig)) {
      // With spacers: spacers sit together without gaps, only letters get distributed spacing
      const spacerCount = letters.length - 1;
      spacerGroupWidth = spacerCount * spacerBeadWidth; // Spacers with no gaps between them
      totalContentWidth = totalLetterWidth + spacerGroupWidth;
      gapBetweenLetters = 0; // No gaps between individual elements initially
    } else {
      // No spacers: letters should be close together with minimal gap
      gapBetweenLetters = 0; // No gaps between letters when no spacers
      totalContentWidth = totalLetterWidth;
    }

    // Add letter spacing value (percentage-based extra spacing)
    // - this will be distributed only among letters
    const letterSpacingValue = (totalContentWidth * (beadConfig.letter_spacing || 0)) / 100;
    const finalContentWidth = (totalContentWidth + letterSpacingValue) * scale;

    // Calculate positions based on actual letter dimensions
    // pathLength and scaledPathLength already declared above

    // Find center position on path (always center letters along the path)
    const centerPosition = 0.5;

    // Reverse letters array to fix display order
    const reversedLetters = [...letters].reverse();
    const reversedDimensions = [...letterDimensions].reverse();

    // Calculate cumulative positions based on actual letter widths
    const positions = [];
    const occupiedPositions = [];

    if (shouldRenderSpacers(beadConfig)) {
      // New approach: distribute extra spacing only among letters, keep spacers grouped
      const extraSpacing = letterSpacingValue;
      const letterGap = letters.length > 1 ? extraSpacing / (letters.length - 1) : 0;

      // Calculate letter positions with distributed spacing
      let currentOffset = -(finalContentWidth / 2); // Start from left edge of content

      for (let i = 0; i < reversedLetters.length; i += 1) {
        const letterDim = reversedDimensions[i];

        // Position letter at its center
        const letterCenterOffset = currentOffset + ((letterDim.width * scale) / 2);
        const position = Math.max(
          0,
          Math.min(1, centerPosition + (letterCenterOffset / scaledPathLength)),
        );
        positions.push(position);

        // Move to next letter position
        currentOffset += letterDim.width * scale;

        // Add spacer width and distributed gap for next letter (except after last letter)
        if (i < reversedLetters.length - 1) {
          currentOffset += spacerBeadWidth * scale; // Account for spacer width
          currentOffset += letterGap * scale;
        }
      }
    } else {
      // Original logic for when no spacers are used
      let currentOffset = -(finalContentWidth / 2); // Start from left edge of content

      for (let i = 0; i < reversedLetters.length; i += 1) {
        const letterDim = reversedDimensions[i];

        // Position letter at its center
        const letterCenterOffset = currentOffset + ((letterDim.width * scale) / 2);
        const position = Math.max(
          0,
          Math.min(1, centerPosition + (letterCenterOffset / scaledPathLength)),
        );
        positions.push(position);

        // Move to next letter position
        currentOffset += letterDim.width * scale;

        // Add gap for next letter (except after last letter)
        if (i < reversedLetters.length - 1 && gapBetweenLetters > 0) {
          currentOffset += gapBetweenLetters * scale;
        }
      }
    }

    // Place letters and spacers using reversed letters array
    for (let i = 0; i < reversedLetters.length; i += 1) {
      // Create letter bead at calculated position
      const letterPosition = Math.max(0, Math.min(1, positions[i]));
      const letterPathPos = letterPosition * pathLength;

      renderLetterBead(
        beadContainer,
        pathElement,
        reversedLetters[i],
        letterPathPos,
        beadConfig,
        availableLetters,
        letterBeadClass,
      );

      // Use actual letter dimensions for occupied position calculation
      const letterDim = reversedDimensions[i];
      const actualLetterBeadSize = Math.max(letterDim.width, letterDim.height);
      occupiedPositions.push({
        position: letterPosition,
        type: 'letter',
        size: actualLetterBeadSize, // Already in unscaled units
      });

      // Add spacer between letters (not after the last letter) - only if spacers should be rendered
      if (i < reversedLetters.length - 1 && shouldRenderSpacers(beadConfig)) {
        // Calculate spacer position based on current and next letter positions
        const currentLetterPos = positions[i];
        const nextLetterPos = positions[i + 1];
        const spacerPosition = (currentLetterPos + nextLetterPos) / 2; // Midpoint between letters
        const spacerPathPos = spacerPosition * pathLength;

        renderSpacerBead(beadContainer, pathElement, spacerPathPos, beadConfig, spacerBeadClass);
        occupiedPositions.push({
          position: spacerPosition,
          type: 'spacer',
          size: spacerSize / scale, // Store in unscaled units for consistent calculation
        });
      }
    }

    // After placing letters and spacers, fill remaining space with regular beads
    fillRemainingPathWithRegularBeads(
      beadContainer,
      pathElement,
      occupiedPositions,
      beadConfig,
      scale,
      blankBeadClass,
    );
  }

  function renderBeadsInContainer(
    container,
    text,
    beadConfig,
    availableLetters,
    options = {},
  ) {
    const {
      svgContainerClass = 'bead-path-svg-container',
      beadContainerClass = 'bead-path-bead-container',
      letterBeadClass = 'bead-path-letter-bead',
      spacerBeadClass = 'bead-path-spacer-bead',
      blankBeadClass = 'bead-path-blank-bead',
    } = options;

    const svgGuideContent = beadConfig.svg_guide_content;

    if (!svgGuideContent) {
      console.warn('[BeadPath] No SVG guide content');
      return;
    }

    // Clear existing beads
    const beadContainer = container.querySelector(`.${beadContainerClass}`);
    if (beadContainer) {
      clearBeadsInContainer(beadContainer, [letterBeadClass, spacerBeadClass, blankBeadClass]);
    }

    // Create SVG container for the path
    const svgContainer = createSVGContainer(container, beadConfig, svgContainerClass);
    const pathElement = createSVGPath(svgContainer, svgGuideContent);

    if (!pathElement) {
      console.warn('[BeadPath] Failed to create path element');
      return;
    }

    // Create bead container
    const newBeadContainer = createBeadContainer(container, beadContainerClass);

    if (text && text.trim()) {
      // Process text through icon mapping first (Unicode -> title conversion)
      const processedText = processTextWithIconMapping(text);

      // Then tokenize the processed text to handle special tokens
      const letters = tokenizeText(processedText, availableLetters);
      const scale = getResponsiveScale(container, beadConfig);
      renderLetterBeadsWithRegularBeads(
        newBeadContainer,
        pathElement,
        letters,
        beadConfig,
        scale,
        availableLetters,
        letterBeadClass,
        spacerBeadClass,
        blankBeadClass,
      );
    } else {
      // Render blank beads to fill the path
      renderBlankBeadsOnPath(newBeadContainer, pathElement, beadConfig, blankBeadClass);
    }

    // Apply container shadow based on light source
    applyDropShadowToContainer(container, beadConfig);
  }

  function clearBeadElements(containers, beadClasses = ['bead-path-blank-bead', 'bead-path-letter-bead', 'bead-path-spacer-bead']) {
    containers.forEach((container) => {
      if (container) {
        const beadContainer = container.querySelector('.bead-path-bead-container');
        if (beadContainer) {
          clearBeadsInContainer(beadContainer, beadClasses);
        }
      }
    });
  }

  function updateBeadPositionsOnResize(
    containers,
    currentText,
    beadConfig,
    availableLetters,
    options = {},
  ) {
    containers.forEach((container) => {
      if (container) {
        renderBeadsInContainer(container, currentText, beadConfig, availableLetters, options);
      }
    });
  }

  function renderBeadsAfterCoreImagesLoad(
    container,
    text,
    beadConfig,
    availableLetters,
    options = {},
    callback = null,
  ) {
    // Get the core images that get repeated
    let regularBeadImages = [];
    if (Array.isArray(beadConfig.beads) && beadConfig.beads.length > 0) {
      // Collect all bead images from array
      regularBeadImages = beadConfig.beads.map((bead) => (
        typeof bead === 'string' ? bead : bead?.url
      )).filter(Boolean);
    } else if (beadConfig.beads && typeof beadConfig.beads === 'object') {
      const [regularBeadData] = Object.values(beadConfig.beads);
      const regularBeadImage = typeof regularBeadData === 'string' ? regularBeadData : regularBeadData?.url;
      if (regularBeadImage) {
        regularBeadImages = [regularBeadImage];
      }
    }
    const spacerImage = beadConfig.spacer_image;

    const imagesToLoad = [...regularBeadImages, spacerImage].filter(Boolean);

    if (imagesToLoad.length === 0) {
      // No images to preload, render immediately
      renderBeadsInContainer(container, text, beadConfig, availableLetters, options);
      if (callback) callback();
      return;
    }

    let loadedCount = 0;
    const totalImages = imagesToLoad.length;

    const checkComplete = () => {
      loadedCount += 1;
      if (loadedCount >= totalImages) {
        renderBeadsInContainer(container, text, beadConfig, availableLetters, options);
        if (callback) callback();
      }
    };

    // Preload each core image
    imagesToLoad.forEach((imageUrl) => {
      const img = new Image();
      img.onload = checkComplete;
      img.onerror = checkComplete; // Continue even if image fails
      img.src = imageUrl;
    });
  }

  // Public API
  return {
    // Core rendering functions
    renderBeadsInContainer,
    renderBeadsAfterCoreImagesLoad,
    renderLetterBeadsWithRegularBeads,
    renderBlankBeadsOnPath,
    fillRemainingPathWithRegularBeads,

    // Individual bead rendering
    renderLetterBead,
    renderSpacerBead,
    renderBlankBead,

    // Utility functions
    decodeEscapeSequence,
    getMetaobjectKey,
    processTextWithIconMapping,
    tokenizeText,
    getResponsiveScale,
    calculateBeadDimensions,
    getRenderer,
    fixDoubleEscapedUnicode,
    hex2rgb,
    debounce,
    createSVGContainer,
    createSVGPath,
    createBeadContainer,
    clearBeadsInContainer,
    clearBeadElements,
    updateBeadPositionsOnResize,

    // Positioning and effects
    getLightSourcePosition,
    getRegularBeadRotation,
    getPathBasedRotation,
    getPathTangentRotation,
    applyPerpendicularRandomOffset,
    applyDropShadowToContainer,

    // Configuration
    getConfig: () => config,
    setConfig: (newConfig) => Object.assign(config, newConfig),
  };
}());
